import { ClickAwayListener, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import ButtonSubAction from "../../../../components/buttons/ButtonSubAction";
import {
    CloseIcon,
    NotificationIcon,
} from "../../../../components/buttons/IconButtons";
import NotInformation from "../../../../components/NotInformation";
import { CustomStylePaperNotifications } from "../../../../components/Styles";
import { NotificationsButtonApp } from "../Functionality/NotificationsButtonApp";
import { notificationsItems, NOTIFICATION_TABS_MODEL } from "../Model/NotificationsModel";
import NotificationDetail from "./NotificationDetail";
import CustomTabContainer from "../../../../components/CustomTabContainer";
import CustomTab from "../../../../components/CustomTab";


/**
 *
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * Renders a single report item using the logic from NotificationsButtonApp.
 * @param {Object} props
 * @param {Object} props.report - The report object
 * @param {number} props.idSelected - Current selected tab id
 * @param {Function} props.getTitleReport - Function to get the report title
 * @param {Function} props.getDescReport - Function to get the report description
 * @param {Function} props.handleDownload - Function to handle report download
 * @param {Function} props.calculateTime - Function to calculate elapsed time
 */

/**
 * Notifications Button Component
 * Handles the floating notifications panel and tabbed navigation.
 */
const NotificationsButton = () => {
    const {
        dispatch,
        navigate,
        // states
        open,
        selected,
        setSelected,
        notifications,

        // setters & handlers
        handleToggle,
        handleClose,
        handleMarkAllReportsAsRead,
        showButton,
        handleMarkOneAsRead
    } = NotificationsButtonApp();


    const { t } = useTranslation();

    /**
     * Render notifications depending on selected tab
     */
    const renderNotifications = () => {
        if (notifications[NOTIFICATION_TABS_MODEL.ALL.name].list.length === 0) return <NotInformation text={t("no_pending_notifications")} />;

        return notifications[selected].list.length > 0 ? (
            notifications[selected].list.map((e) => (<>
                <NotificationDetail
                    dispatch={dispatch}
                    navigate={navigate}
                    key={e.id}
                    {...e}
                    handleToggle={handleToggle}
                    onMarkAsRead={() => handleMarkOneAsRead(e.id, e.key)}
                    readIndicatorClass={e.readIndicatorClass}
                    unreadIndicatorClass={e.unreadIndicatorClass}
                />
            </>

            ))
        ) : (
            <NotInformation text={t("no_pending_notifications")} />
        );
    };

    if (!showButton) return null;

    return (
        <>
            {/* Floating button */}
            <div
                className="d-flex align-items-center justify-content-center gap-2 py-3 px-4 rounded-pill background-shades-900 position-fixed bottom-0 end-0 m-3 cursor-pointer z-index-3"
                onClick={handleToggle}
            >
                <NotificationIcon className="icon-color-general-00 position-relative" />
                {notifications[NOTIFICATION_TABS_MODEL.ALL.name].unviewed > 0 &&
                    <div
                        className="position-absolute d-flex justify-content-center align-items-center t-10px l-35px w-20px h-20px fs-10px background-error-code-600 font-color-general-00 rounded-circle">
                        {notifications[NOTIFICATION_TABS_MODEL.ALL.name].unviewed}
                    </div>
                }
                <p className="font-color-general-00 ibl-font-w-500">
                    {t("notifications")}
                </p>
            </div>

            {/* Floating notifications panel */}
            {open && (
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper className="px-3" style={CustomStylePaperNotifications}>
                        <div className="sticky-top background-general-00 pt-3">
                            {/* Header */}
                            <div className="d-flex justify-content-between mb-2">
                                <div className="d-flex gap-2">
                                    <NotificationIcon className="icon-color-general-950 translate-y-3px" />
                                    <div className="fs-20px ibl-font-w-600">{t("notifications")}</div>
                                </div>
                                <div className="d-flex gap-1">
                                    <ButtonSubAction
                                        id="btn_mark_all_as_read"
                                        name="mark_all_as_read"
                                        className="btn-href-general-hover-950 pt-1"
                                        onClick={handleMarkAllReportsAsRead}
                                    />
                                    <div className="w-h-24px translate-y-3px btn-icon-cancel">
                                        <CloseIcon
                                            onClick={handleClose}
                                            className="w-h-12px"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Tabs */}
                            <CustomTabContainer
                                activeTab={selected}
                                setActiveTab={setSelected}
                                className="rounded-top px-3"
                            >
                                {notificationsItems.map((item) => (
                                    <CustomTab
                                        key={item.key}
                                        id={item.key}
                                        title={t(item.translation)}
                                        newsNumber={notifications[item.key].unviewed || 0}
                                        icon={item.icon}
                                        selected={selected === item.key}
                                        onClick={() => setSelected(item.key)}
                                    />
                                ))}
                            </CustomTabContainer>
                        </div>

                        {/* Notifications container */}
                        <div className="overflow-y-auto border border-top-0 mb-3 rounded-bottom maxh-500px notifications-container ">
                            {renderNotifications()}
                        </div>
                    </Paper>
                </ClickAwayListener>
            )}
        </>
    );
};

export default NotificationsButton;
